import React, { useState, useEffect, useRef } from 'react';
import { X, Minimize } from 'lucide-react';
import TerminalPrompt from './TerminalPrompt';
import TerminalOutput from './TerminalOutput';
import { executeCommand } from '../utils/CommandProcessor';
import SoundManager from '../utils/SoundManager';
import { OutputItem } from '../types';

interface TerminalProps {
  onClose: () => void;
  onOpenPage: (pageId: string) => void;
}

const Terminal: React.FC<TerminalProps> = ({ onClose, onOpenPage }) => {
  const [output, setOutput] = useState<OutputItem[]>([
    { type: 'text', content: '*** TERMINAL 64 BASIC V2 ***', key: 'welcome-1' },
    { type: 'text', content: '64K RAM SYSTEM 38911 BASIC BYTES FREE', key: 'welcome-2' },
    { type: 'text', content: 'READY.', key: 'welcome-3' },
    { type: 'text', content: 'TYPE "', key: 'welcome-4a', inline: true },
    { type: 'link', content: 'HELP', key: 'welcome-4b', inline: true, onClick: () => handleCommand('HELP') },
    { type: 'text', content: '" FOR AVAILABLE COMMANDS OR "', key: 'welcome-4c', inline: true },
    { type: 'link', content: 'DIR', key: 'welcome-4d', inline: true, onClick: () => handleCommand('DIR') },
    { type: 'text', content: '" TO SEE PAGES', key: 'welcome-4e', inline: true },
  ]);
  const [commandHistory, setCommandHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const contentRef = useRef<HTMLDivElement>(null);
  const terminalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isMinimized, setIsMinimized] = useState(false);

  // Auto-scroll to the bottom when output changes
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [output]);

  // Keep input focused and handle keypress events for the entire terminal
  useEffect(() => {
    // Detect if device is mobile/tablet (screen width < 768px)
    const isMobileOrTablet = () => window.innerWidth < 768;
    
    const focusInput = () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    };

    // Focus input on mount only on desktop
    if (!isMobileOrTablet()) {
      focusInput();
    }

    // Handle click to focus (explicit user action - always allowed)
    const handleClick = () => {
      // Only focus input on desktop, not on mobile/tablet
      if (!isMobileOrTablet()) {
        focusInput();
      }
    };
    
    // Handle window focus (only on desktop)
    const handleWindowFocus = () => {
      if (!isMobileOrTablet()) {
        focusInput();
      }
    };

    const terminalElement = terminalRef.current;
    if (terminalElement) {
      terminalElement.addEventListener('click', handleClick);
    }

    // Re-focus when the window gains focus (only on desktop)
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      if (terminalElement) {
        terminalElement.removeEventListener('click', handleClick);
      }
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, []);

  const handleCommand = (command: string) => {
    // Add command to history
    if (command.trim() !== '') {
      setCommandHistory(prev => [command, ...prev]);
      setHistoryIndex(-1);
    }

    // Process the command
    const result = executeCommand(command, output, onOpenPage);
    setOutput(result);

    // Play sound effect
    SoundManager.playSound('command');
  };

  const handleMinimize = () => {
    setIsMinimized(!isMinimized);
    SoundManager.playSound('toggle');
  };

  return (
    <div className={`fixed inset-0 flex items-center justify-center z-40 p-0 transition-all duration-500 ${isMinimized ? 'translate-y-[calc(100%-40px)]' : ''}`}>
      <div 
        ref={terminalRef} 
        className="w-full h-full sm:w-full sm:h-full flex flex-col bg-[var(--window-bg)] border-[5px] sm:border-[15px] border-[var(--terminal-border)] rounded-lg overflow-hidden shadow-[0_0_20px_rgba(95,233,121,0.15),inset_0_0_10px_rgba(0,0,0,0.5)] transition-all duration-500"
        tabIndex={0} // Make terminal div focusable
      >
        <div 
          className="terminal-titlebar cursor-move"
          onDoubleClick={handleMinimize}
        >
          <div className="terminal-btn terminal-close" onClick={onClose}>
            <X size={10} className="opacity-0 hover:opacity-100" />
          </div>
          <div className="terminal-btn terminal-minimize" onClick={handleMinimize}>
            <Minimize size={10} className="opacity-0 hover:opacity-100" />
          </div>
          <div className="terminal-title text-xs">gb4k.fr</div>
        </div>
        
        <div 
          ref={contentRef}
          className="terminal-content flex-grow crt-flicker relative"
        >
          <TerminalOutput output={output} onOpenPage={onOpenPage} />
          <TerminalPrompt 
            onCommand={handleCommand} 
            commandHistory={commandHistory}
            historyIndex={historyIndex}
            setHistoryIndex={setHistoryIndex}
            inputRef={inputRef}
          />
          <div className="scanlines absolute inset-0 pointer-events-none"></div>
        </div>
      </div>
    </div>
  );
};

export default Terminal;